import React, { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Home, Calendar, Camera, MessageSquare, MoreHorizontal,
  FileText, Utensils, Settings, LogOut, Bug, Clock
} from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { useTheme } from '@/lib/ThemeContext';
import { useEnabledModulesForUser } from '@/hooks/useBranchModules';

const NAV_ITEMS_BASE = [
  { name: 'Start', icon: Home, page: '/TeacherDashboard', tutorialId: 'nav-home' },
  { name: 'Dziennik', icon: Calendar, page: '/DailyUpdates', tutorialId: 'nav-daily' },
  { name: 'Zdjęcia', icon: Camera, page: '/PhotosManagement', tutorialId: 'nav-photos', module: 'media' },
  { name: 'Wiadomości', icon: MessageSquare, page: '/Announcements', tutorialId: 'nav-messages' },
];

const MORE_ITEMS_BASE = [
  { name: 'Mój grafik', icon: Clock, page: '/MySchedule', tutorialId: 'nav-my-schedule', module: 'grafik' },
  { name: 'Szablony', icon: FileText, page: '/Templates', tutorialId: 'nav-templates' },
  { name: 'Jadłospis', icon: Utensils, page: '/TeacherMenu', tutorialId: 'nav-menu', module: 'jadlospis' },
  { name: 'Ustawienia', icon: Settings, page: '/Settings', tutorialId: 'nav-settings' },
];

export default function TeacherBottomNav({ onLogout, onReportBug }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const { getActiveColors } = useTheme();
  const themeColors = getActiveColors();
  const { isModuleEnabled } = useEnabledModulesForUser();

  const navItems = useMemo(
    () => NAV_ITEMS_BASE.filter((item) => !item.module || isModuleEnabled(item.module)),
    [isModuleEnabled]
  );
  const moreItems = useMemo(
    () => MORE_ITEMS_BASE.filter((item) => !item.module || isModuleEnabled(item.module)),
    [isModuleEnabled]
  );

  const isActive = (page) => {
    return location.pathname === page || location.pathname === page.toLowerCase();
  };

  const handleNavClick = (page) => {
    navigate(page);
  };

  const handleMoreItemClick = (page) => {
    setMoreOpen(false);
    navigate(page);
  };

  return (
    <>
      {/* Bottom Navigation Bar - Uses theme colors */}
      <nav className={cn("fixed bottom-0 left-0 right-0 z-50 safe-area-bottom", themeColors.headerBg)}>
        <div className="flex items-center justify-around h-16 px-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.page);
            return (
              <button
                key={item.page}
                onClick={() => handleNavClick(item.page)}
                data-tutorial={item.tutorialId}
                className={cn(
                  "flex flex-col items-center justify-center flex-1 h-full gap-1 transition-colors",
                  active ? "text-white" : "text-white/70 hover:text-white"
                )}
              >
                <Icon className="w-5 h-5" />
                <span className={cn(
                  "text-xs",
                  active ? "font-semibold" : "font-medium"
                )}>
                  {item.name}
                </span>
              </button>
            );
          })}

          {/* More Button */}
          <button
            onClick={() => setMoreOpen(true)}
            className={cn(
              "flex flex-col items-center justify-center flex-1 h-full gap-1 transition-colors",
              moreOpen ? "text-white" : "text-white/70 hover:text-white"
            )}
          >
            <MoreHorizontal className="w-5 h-5" />
            <span className="text-xs font-medium">Inne</span>
          </button>
        </div>
      </nav>

      {/* More Sheet (Bottom Drawer) */}
      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl">
          <SheetHeader className="pb-4">
            <SheetTitle>Inne opcje</SheetTitle>
          </SheetHeader>

          <div className="space-y-2 pb-8">
            {moreItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.page);
              return (
                <button
                  key={item.page}
                  onClick={() => handleMoreItemClick(item.page)}
                  className={cn(
                    "flex items-center gap-4 w-full p-4 rounded-xl transition-colors",
                    active
                      ? "bg-emerald-50 text-emerald-600"
                      : "hover:bg-slate-50 text-slate-700"
                  )}
                >
                  <div className={cn(
                    "p-2 rounded-lg",
                    active ? "bg-emerald-100" : "bg-slate-100"
                  )}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <span className={cn(
                    "font-medium",
                    active && "font-semibold"
                  )}>
                    {item.name}
                  </span>
                </button>
              );
            })}

            {/* Divider */}
            <div className="border-t border-slate-200 my-4" />

            {/* Report Bug Button */}
            <button
              onClick={() => {
                setMoreOpen(false);
                onReportBug?.();
              }}
              className="flex items-center gap-4 w-full p-4 rounded-xl transition-colors hover:bg-amber-50 text-amber-700"
            >
              <div className="p-2 rounded-lg bg-amber-100">
                <Bug className="w-5 h-5" />
              </div>
              <span className="font-medium">Zgłoś problem</span>
            </button>

            {/* Logout Button */}
            <button
              onClick={() => {
                setMoreOpen(false);
                onLogout?.();
              }}
              className="flex items-center gap-4 w-full p-4 rounded-xl transition-colors hover:bg-red-50 text-red-600"
            >
              <div className="p-2 rounded-lg bg-red-100">
                <LogOut className="w-5 h-5" />
              </div>
              <span className="font-medium">Wyloguj</span>
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
